/**
 * Pryme secret keys: one string per credential, with the environment in the prefix.
 *
 *   sk_test_<base64url(JSON)>   sandbox
 *   sk_live_<base64url(JSON)>   production
 *
 * Two kinds, told apart by `typ` inside, so the SDK can refuse the wrong one with a clear message:
 *
 *  - `service` (`PRYME_SECRET_KEY`, used with the public `PRYME_SERVICE_ID`): the service's signing
 *    key (key id + Ed25519 secret), Pryme's verification key for that same key generation (key id +
 *    Ed25519 public key, the trust anchor), and the API base and audience of the issuing deployment.
 *  - `agent` (`PRYME_AGENT_SECRET_KEY`): the agent id, its key id and Ed25519 secret, and the API
 *    base and audience. Assembled by the agent itself after pairing; the private key never leaves it.
 *
 * Verification keys travel inside the service secret, so they stay pinned: they arrive once,
 * through the same authenticated credentials panel as the secret, and are never fetched or
 * accepted at runtime. Each service secret trusts only its own generation's verification key;
 * Pryme answers every request with the key of the generation that signed it (ADR-0013).
 */
import { b64url } from './crypto.js';
const PREFIX = { sandbox: 'sk_test_', production: 'sk_live_' };
const KEY = /^[A-Za-z0-9_-]{43}$/;
const ID = /^[A-Za-z0-9_.:-]{1,128}$/;
/** A secret carries its own generation's verification key; a few more are allowed for explicit setups. */
const MAX_VERIFICATION_KEYS = 4;
const ENV_VAR = { service: 'PRYME_SECRET_KEY', agent: 'PRYME_AGENT_SECRET_KEY' };
const fail = (why) => {
    throw new Error(`invalid Pryme secret key: ${why}`);
};
const encode = (environment, body) => `${PREFIX[environment]}${b64url.encode(new TextEncoder().encode(JSON.stringify(body)))}`;
/** `sandbox` for sk_test_…, `production` for sk_live_…, otherwise null. */
export function secretKeyEnvironment(value) {
    if (typeof value !== 'string')
        return null;
    const v = value.trim();
    if (v.startsWith(PREFIX.sandbox))
        return 'sandbox';
    if (v.startsWith(PREFIX.production))
        return 'production';
    return null;
}
/** True for anything shaped like a Pryme secret key (sk_test_… or sk_live_…), service or agent. */
export const isSecretKey = (value) => secretKeyEnvironment(value) !== null;
function decode(value, want) {
    const v = typeof value === 'string' ? value.trim() : '';
    const environment = secretKeyEnvironment(v) ?? fail('expected a value starting with sk_test_ (sandbox) or sk_live_ (production)');
    let body;
    try {
        body = JSON.parse(new TextDecoder().decode(b64url.decode(v.slice(PREFIX[environment].length))));
    }
    catch {
        return fail('it is truncated or was not copied completely');
    }
    if (body === null || typeof body !== 'object' || body.v !== 1)
        fail('unsupported version');
    if (body.typ !== want) {
        if (body.typ === 'service' || body.typ === 'agent') {
            fail(`this is ${body.typ === 'agent' ? 'an agent' : 'a service'} secret key (${ENV_VAR[body.typ]}); ${want === 'service' ? 'a service backend needs its service secret key (PRYME_SECRET_KEY)' : 'an agent needs its own agent secret key (PRYME_AGENT_SECRET_KEY)'}`);
        }
        fail('unknown key type');
    }
    const str = (k, re) => {
        const x = body[k];
        if (typeof x !== 'string' || !x || (re && !re.test(x)))
            fail(`missing or malformed ${k}`);
        return x;
    };
    const url = (x, what) => {
        try {
            const u = new URL(x);
            if (u.protocol !== 'https:' && !['localhost', '127.0.0.1', '[::1]'].includes(u.hostname))
                fail(`the ${what} must use https`);
        }
        catch (e) {
            if (e.message.startsWith('invalid Pryme'))
                throw e;
            fail(`malformed ${what}`);
        }
        return x;
    };
    const api = url(str('api'), 'API base');
    return { environment, body, str, api, url };
}
export function encodeServiceSecret(s) {
    return encode(s.environment, {
        v: 1,
        typ: 'service',
        svc: s.serviceId,
        api: s.apiBase,
        aud: s.apiAudience,
        ...(s.issuer ? { iss: s.issuer } : {}),
        kid: s.signingKey.keyId,
        sk: s.signingKey.secretKey,
        vk: s.prymeKeys.map((k) => ({ kid: k.keyId, key: k.publicKey })),
    });
}
/** Parse and validate a service secret key. Throws with a plain message on anything malformed. */
export function parseServiceSecret(value) {
    const { environment, body, str, api, url } = decode(value, 'service');
    const vk = body.vk;
    if (!Array.isArray(vk) || !vk.length || vk.length > MAX_VERIFICATION_KEYS)
        fail('no verification keys');
    const prymeKeys = vk.map((k) => {
        if (typeof k?.kid !== 'string' || !ID.test(k.kid) || typeof k.key !== 'string' || !KEY.test(k.key))
            fail('malformed verification key');
        return { keyId: k.kid, publicKey: k.key };
    });
    return {
        serviceId: str('svc', ID),
        environment,
        apiBase: api,
        apiAudience: str('aud', ID),
        ...(body.iss !== undefined ? { issuer: url(str('iss'), 'issuer') } : {}),
        signingKey: { keyId: str('kid', ID), secretKey: str('sk', KEY) },
        prymeKeys,
    };
}
export function encodeAgentSecret(s) {
    return encode(s.environment, { v: 1, typ: 'agent', agt: s.agentId, api: s.apiBase, aud: s.audience, kid: s.keyId, sk: s.secretKey });
}
/** Parse and validate an agent secret key. Throws with a plain message on anything malformed. */
export function parseAgentSecret(value) {
    const { environment, str, api } = decode(value, 'agent');
    return { agentId: str('agt', ID), environment, apiBase: api, audience: str('aud', ID), keyId: str('kid', ID), secretKey: str('sk', KEY) };
}
